import { cn } from '@/lib/utils';

/** Product name as users and their clients see it. */
export const PRODUCT_NAME = 'Yedo Books';
/** The studio behind it, shown in the "by" line and the footer. */
export const MAKER_NAME = 'Gear Up Studios';

/**
 * The Yedo mark: an open book whose two pages meet in a "Y", white on a blue tile. The favicon,
 * the collapsed sidebar and the wordmark all use it.
 */
export function YedoMark({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 32 32" aria-hidden="true" className={cn('size-6 shrink-0', className)}>
      <defs>
        <linearGradient id="yedo-tile" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#388bff" />
          <stop offset="1" stopColor="#0055cc" />
        </linearGradient>
      </defs>
      <rect width="32" height="32" rx="7" fill="url(#yedo-tile)" />
      <path d="M6.5 8.5 15 13v11.5l-8.5-4.5z" fill="#fff" />
      <path d="M25.5 8.5 17 13v11.5l8.5-4.5z" fill="#fff" fillOpacity=".72" />
    </svg>
  );
}

/**
 * Wordmark: the mark, "Yedo Books" in bold navy (white on dark), and optionally the
 * "by Gear Up Studios" line under it.
 */
export function Logo({
  onDark = false,
  mark = true,
  byline = false,
  className,
}: {
  onDark?: boolean;
  mark?: boolean;
  byline?: boolean;
  className?: string;
}) {
  return (
    <span
      role="img"
      aria-label={byline ? `${PRODUCT_NAME} by ${MAKER_NAME}` : PRODUCT_NAME}
      className={cn('inline-flex items-center gap-2 whitespace-nowrap', className)}
    >
      {mark ? <YedoMark className={byline ? 'size-8' : undefined} /> : null}
      <span aria-hidden="true" className="flex flex-col leading-none">
        <span
          className={cn(
            'text-[1.1rem] font-bold tracking-[-0.02em]',
            onDark ? 'text-white' : 'text-brand-navy',
          )}
        >
          Yedo <span className={onDark ? 'text-brand-blue-200' : 'text-primary'}>Books</span>
        </span>
        {byline ? (
          <span
            className={cn(
              'mt-1 text-[0.68rem] font-medium tracking-wide',
              onDark ? 'text-brand-blue-100/80' : 'text-muted-foreground',
            )}
          >
            by {MAKER_NAME}
          </span>
        ) : null}
      </span>
    </span>
  );
}
